/**
 * Development-only debug utilities
 * These are automatically excluded from production builds
 */

const isDevelopment = import.meta.env.DEV;

/**
 * Development-only console.log
 * Automatically removed in production builds
 */
export const debugLog = (...args: any[]) => {
  if (isDevelopment) {
    console.log(...args);
  }
};

/**
 * Development-only console.warn
 * Automatically removed in production builds
 */
export const debugWarn = (...args: any[]) => {
  if (isDevelopment) {
    console.warn(...args);
  }
};

/**
 * Development-only console.error
 * In production, errors are still logged but without extra debug info
 */
export const debugError = (message: string, ...args: any[]) => {
  if (isDevelopment) {
    console.error(message, ...args);
  } else {
    // In production, log minimal error info
    console.error(message);
  }
};

/**
 * Development-only console.group
 */
export const debugGroup = (label: string) => {
  if (isDevelopment) {
    console.group(label);
  }
};

/**
 * Development-only console.groupEnd
 */
export const debugGroupEnd = () => {
  if (isDevelopment) {
    console.groupEnd();
  }
};

/**
 * Development-only performance timing
 */
export const debugTime = (label: string) => {
  if (isDevelopment) {
    console.time(label);
  }
};

export const debugTimeEnd = (label: string) => {
  if (isDevelopment) {
    console.timeEnd(label);
  }
};

/**
 * Install global debug functions (dev only)
 * These are the functions that were in App.tsx
 */
export const installDebugFunctions = (
  documentState: any,
  projectManager: any,
  storageCoordinator: any,
) => {
  if (!isDevelopment) {
    return;
  }

  // Debug function to inspect project state in storage
  (window as any).debugProjectState = async () => {
    try {
      const projectId = projectManager.getCurrentProjectId();
      if (!projectId) {
        debugLog("No current project");
        return;
      }

      const result = await storageCoordinator.load(
        `project_${projectId}_state`,
      );
      if (!result.success || !result.data) {
        debugLog("Failed to load project state");
        return;
      }

      debugLog("Current Project State:", result.data);
      debugLog("Project State Structure:", {
        hasScenes: !!result.data.scenes,
        scenesKeys: result.data.scenes ? Object.keys(result.data.scenes) : [],
        topLevelKeys: Object.keys(result.data),
        spacesCount: result.data.scenes?.spaces
          ? Object.keys(result.data.scenes.spaces).length
          : 0,
        pagesCount: result.data.scenes?.pages
          ? Object.keys(result.data.scenes.pages).length
          : 0,
      });

      return result.data;
    } catch (error) {
      debugError("Error inspecting project state:", error);
    }
  };

  // Debug function to inspect document state in Redux
  (window as any).debugDocumentState = () => {
    try {
      debugLog("Current Redux Document State:", documentState);

      if (documentState.spaces) {
        debugLog("Spaces in Redux:", Object.keys(documentState.spaces));
      } else {
        debugLog("No spaces in Redux state");
      }

      if ((documentState as any).pages) {
        debugLog("Pages in Redux:", Object.keys((documentState as any).pages));
      } else {
        debugLog("No pages in Redux state");
      }

      return documentState;
    } catch (error) {
      debugError("Error inspecting document state:", error);
    }
  };

  debugLog("Debug functions installed:");
  debugLog("- window.debugProjectState(): Inspect project state in storage");
  debugLog("- window.debugDocumentState(): Inspect document state in Redux");
};

/**
 * Remove global debug functions (for cleanup)
 */
export const removeDebugFunctions = () => {
  if (isDevelopment) {
    delete (window as any).debugProjectState;
    delete (window as any).debugDocumentState;
  }
};
